import { backToMainMessage, switchMenu, toggleMenuModeSection } from './legacyBridge/legacyActions'
import { getMsgDeskRuntime } from './runtime/MsgDeskRuntime'
import { selectDeskIdentity, treeStore } from './state'
import { legacySwitcherForDeskMode, type DeskViewMode } from './contracts/deskNavigation'

export * from './contracts/deskNavigation'

export type DeskNavigationRequest = {
  mode: DeskViewMode
  target?: unknown
  /**
   * Folder-mode section to open directly (favs/drafts/trash only):
   * 0 = answers, 1 = submsges. Requires a real `target` element — the chained
   * legacy section toggle needs a legacy event target. Ignored elsewhere.
   */
  section?: 0 | 1
}

let navigationSequence = 0

export function getDeskNavigationSequence(): number {
  return navigationSequence
}

/**
 * S1: legacy's ACTUAL desk mode (dsk_mode), independent of the projected
 * store mode. The SSR route seeds the store mode to the route target before
 * legacy has navigated, so route settling must consult this instead of the
 * projection. Empty string until the runtime is live.
 */
export function currentLegacyDeskMode(): string {
  try {
    const runtime = getMsgDeskRuntime()
    const modes = runtime.getLegacyObject<any>('msgdsk')?.obj?.('modes')
      || runtime.getLegacyObject<any>('dsk')?.obj?.('modes')
    const modeFn = modes?.dsk_mode || runtime.getLegacyObject<any>('dsk_mode')
    return typeof modeFn === 'function' ? String(modeFn() || '') : ''
  } catch {
    return ''
  }
}

// FOLDER-SECTION CHAIN: after a folder switch settles, replay the exact
// section toggle the content page's Answers/Msges buttons dispatch
// (mev_msg_toggle_menu_subs_evm with an explicit section id), so legacy loads
// and shows the requested section. React's activeSection was already set at
// dispatch time; this keeps legacy paging/tree state in agreement. Waits for
// legacy idle so it never races the switch's own initial load.
function settleFolderSection(mode: DeskViewMode, section: 0 | 1, target: unknown, sequence: number, attempt = 0): void {
  const runtime = getMsgDeskRuntime()
  runtime.setTimeout(() => {
    void runtime.facade.appMsgObj?.legacy?.whenIdle?.(`deskNavigationSection:${mode}:${attempt}`).then(() => {
      if (sequence !== navigationSequence) return
      if (treeStore.getState().renderProjection.mode !== mode) {
        if (attempt >= 5) return
        settleFolderSection(mode, section, target, sequence, attempt + 1)
        return
      }
      toggleMenuModeSection(target, mode, section)
    })
  }, attempt === 0 ? 0 : 100)
}

function stabilizeDeskMode(mode: DeskViewMode, target: unknown, sequence: number, attempt = 0): void {
  const runtime = getMsgDeskRuntime()
  runtime.setTimeout(() => {
    void runtime.facade.appMsgObj?.legacy?.whenIdle?.(`deskNavigation:${mode}:${attempt}`).then(() => {
      if (sequence !== navigationSequence || treeStore.getState().renderProjection.mode === mode) return
      if (attempt >= 2) return
      switchMenu(target, legacySwitcherForDeskMode(mode))
      stabilizeDeskMode(mode, target, sequence, attempt + 1)
    })
  }, attempt === 0 ? 0 : 100)
}

export function dispatchDeskNavigation(request: DeskNavigationRequest): unknown {
  // §0.16 TWO-isMsg-SCOPES: an answer desk has no Messages branch. Reject the
  // unavailable mode outright and coerce a folder request's submsges section
  // to answers (fail-closed canonical desk identity; the menus no longer
  // offer these, so this guards direct/semantic callers).
  const identity = selectDeskIdentity(treeStore.getState())
  if (request.mode === 'submsges' && !identity.canHaveSubmessages) return false
  if (request.section === 1 && !identity.canHaveSubmessages) {
    request = { ...request, section: 0 }
  }

  const sequence = ++navigationSequence
  if (request.mode === 'msg') {
    const result = backToMainMessage(request.target)
    treeStore.mutate.setRenderMode('msg')
    stabilizeDeskMode('msg', request.target, sequence)
    return result
  }

  if (request.mode === 'attachments') {
    treeStore.mutate.setRenderMode('attachments')
    return true
  }

  const result = switchMenu(request.target, legacySwitcherForDeskMode(request.mode))
  const isFolderMode = request.mode === 'favs' || request.mode === 'drafts' || request.mode === 'trash'
  if (isFolderMode && request.section !== undefined && request.target) {
    treeStore.mutate.setActiveSection(request.section === 1 ? 'submsges' : 'answers')
    settleFolderSection(request.mode, request.section, request.target, sequence)
  }
  stabilizeDeskMode(request.mode, request.target, sequence)
  return result
}
